import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/PageHeader";
import { adminBlogPostsQuery } from "@/lib/blog/queries";
import {
  deleteBlogTaxonomyFn,
  saveBlogAuthorFn,
  saveBlogCategoryFn,
  saveBlogTagFn,
} from "@/lib/blog/blog.functions";
import { slugify } from "@/lib/blog/types";

export const Route = createFileRoute("/admin/blog-taxonomy")({ component: TaxonomyPage });

const FIELD = "w-full border border-black/15 px-3 py-2 text-sm focus:border-black/50 focus:outline-none";

function TaxonomyPage() {
  const qc = useQueryClient();
  const { data } = useQuery(adminBlogPostsQuery());
  const invalidate = () => qc.invalidateQueries({ queryKey: ["blog"] });

  const del = useMutation({
    mutationFn: (v: { kind: "category" | "tag" | "author"; id: string }) =>
      deleteBlogTaxonomyFn({ data: v }),
    onSuccess: () => { void invalidate(); toast.success("Deleted"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveCategory = useMutation({
    mutationFn: (v: { name: string; description: string }) =>
      saveBlogCategoryFn({ data: { name: v.name, slug: slugify(v.name), description: v.description } }),
    onSuccess: () => { void invalidate(); toast.success("Category saved"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const saveTag = useMutation({
    mutationFn: (name: string) => saveBlogTagFn({ data: { name, slug: slugify(name) } }),
    onSuccess: () => { void invalidate(); toast.success("Tag saved"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const saveAuthor = useMutation({
    mutationFn: (v: { name: string; role: string; bio: string }) =>
      saveBlogAuthorFn({ data: { name: v.name, slug: slugify(v.name), role: v.role, bio: v.bio } }),
    onSuccess: () => { void invalidate(); toast.success("Author saved"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const [cat, setCat] = useState({ name: "", description: "" });
  const [tag, setTag] = useState("");
  const [author, setAuthor] = useState({ name: "", role: "", bio: "" });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Blog taxonomy"
        subtitle="Categories, tags and authors"
        action={
          <Link to="/admin/blog" className="border border-black/15 px-3 py-2 text-[11px] uppercase tracking-[0.18em]">
            Back to posts
          </Link>
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <section className="flex flex-col gap-3 border border-black/10 bg-white p-4">
          <h3 className="text-sm font-semibold text-black">Categories</h3>
          <ul className="flex flex-col divide-y divide-black/5">
            {(data?.categories ?? []).map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <span>{c.name}<span className="ml-2 font-mono text-[11px] text-black/40">{c.slug}</span></span>
                <button type="button" className="text-[11px] text-red-700" onClick={() => del.mutate({ kind: "category", id: c.id })}>Delete</button>
              </li>
            ))}
          </ul>
          <input className={FIELD} placeholder="Category name" value={cat.name} onChange={(e) => setCat({ ...cat, name: e.target.value })} />
          <input className={FIELD} placeholder="Description" value={cat.description} onChange={(e) => setCat({ ...cat, description: e.target.value })} />
          <button
            type="button"
            className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
            onClick={() => { if (!cat.name.trim()) return; saveCategory.mutate(cat); setCat({ name: "", description: "" }); }}
          >
            Add category
          </button>
        </section>

        <section className="flex flex-col gap-3 border border-black/10 bg-white p-4">
          <h3 className="text-sm font-semibold text-black">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {(data?.tags ?? []).map((t) => (
              <span key={t.id} className="flex items-center gap-2 border border-black/15 px-2 py-1 text-[11px]">
                {t.name}
                <button type="button" className="text-red-700" onClick={() => del.mutate({ kind: "tag", id: t.id })}>×</button>
              </span>
            ))}
          </div>
          <input className={FIELD} placeholder="Tag name" value={tag} onChange={(e) => setTag(e.target.value)} />
          <button
            type="button"
            className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
            onClick={() => { if (!tag.trim()) return; saveTag.mutate(tag); setTag(""); }}
          >
            Add tag
          </button>
        </section>

        <section className="flex flex-col gap-3 border border-black/10 bg-white p-4">
          <h3 className="text-sm font-semibold text-black">Authors</h3>
          <ul className="flex flex-col divide-y divide-black/5">
            {(data?.authors ?? []).map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <span>{a.name}<span className="ml-2 text-[11px] text-black/45">{a.role}</span></span>
                <button type="button" className="text-[11px] text-red-700" onClick={() => del.mutate({ kind: "author", id: a.id })}>Delete</button>
              </li>
            ))}
          </ul>
          <input className={FIELD} placeholder="Author name" value={author.name} onChange={(e) => setAuthor({ ...author, name: e.target.value })} />
          <input className={FIELD} placeholder="Role" value={author.role} onChange={(e) => setAuthor({ ...author, role: e.target.value })} />
          <textarea className={FIELD} rows={3} placeholder="Bio" value={author.bio} onChange={(e) => setAuthor({ ...author, bio: e.target.value })} />
          <button
            type="button"
            className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
            onClick={() => { if (!author.name.trim()) return; saveAuthor.mutate(author); setAuthor({ name: "", role: "", bio: "" }); }}
          >
            Add author
          </button>
        </section>
      </div>
    </div>
  );
}